import { Injectable } from '@angular/core';
import { HttpProvider } from '../http/http';
import { APP_CONFIG, Region } from '../../environment';

import { IFormRequestOptions } from '../formbuilder/form-request-options.interface';
import { FormControlMapper } from '../formbuilder/formcontrol-mapper';
import { FormControlOption, IFormControl } from '../formbuilder/formcontrol.interface';
import { FormValidatorType } from '../formbuilder/form-validator-type';
import { RegistrationFormDynamicField } from '../../model/types/registration-form-dynamic-type';
import { IResponse } from '../../model/api/response.entity.interface';
import { LoggerProvider, LoggerEvent } from '../logger/logger';
import { IApiAuthResponseEntity } from '../../model/api/auth/api-auth-response.entity.interface';
import { DeviceProvider } from '../device';
import { UtilsProvider } from '../myria/utils-provider';
import { ResourceProvider } from '../resource/resource';

/**
 * @description Gets and maps responses from endpoints meant to return form collections/field inputs.
 */
@Injectable()
export class FormControlProvider {
  static readyPromise;
  static readyResolve;

  initialized: boolean = false;
  initializedCustomerSettings: boolean = false;
  fieldsRegistration: any = {};
  fieldsDynamicRegistration: any = {};
  fieldsCustomerBank = {};
  fieldsCustomerCard = {};
  fieldsCustomerIdCountry = {};
  fieldsCustomerIdType = {};
  fieldsCustomer = {};
  fieldsRecipient: any = {};
  idTypeCountry = '';
  fieldsRegistrationCountry;

  constructor(private httpProvider: HttpProvider,
              private resourceProvider: ResourceProvider,
              private deviceProvider: DeviceProvider) { 
    FormControlProvider.readyPromise = new Promise((resolve) => {
      FormControlProvider.readyResolve = resolve;
    });    
  }

  logout() {
    // don't reset the fields since it doesn't make sense to reload them
    // we only need to reset them when we changed the country
  }

  reset() {
    this.initializedCustomerSettings = false;
    this.fieldsCustomerBank = {};
    this.fieldsCustomerCard = {};
    this.fieldsCustomerIdCountry = {};
    this.fieldsCustomerIdType = {};
    this.fieldsCustomer = {};
    this.fieldsRecipient = {};
    this.idTypeCountry = '';
    this.initialized = false;
  }

  /**
   *
   * @param {options} options which define which request should be sent for field definitions
   * @returns {IFormControl[]}
   */
  getFields(options: IFormRequestOptions, throwError: boolean = false, headers = null, receiveCallback = null): Promise<IFormControl[]> {
    return new Promise((resolve, reject) => {
      this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}${options.uri}`, options.params, (res: IResponse) => {

        if (options.uri == "customer-profile"){      
          // OM-5507 - Updating postal code regex to local
          let formField = res.formData.CustomerProfileForm.formFields;
          UtilsProvider.updatePostalCodeRegex(formField, false);
        }

        if (receiveCallback)
          receiveCallback(res);

        return FormControlMapper.fromFormResponse(res);
      }, headers).then((data) => {
        resolve(data);
      }).catch((err) => {
        if (throwError || err.status === 404)
          reject(err);
        else if (err.status !== 401 && err.status !== 403) // these are already handled and shouldn't cause a reload
          resolve(null);
      });
    });
  }
  
  async getFieldsPromiseUrl(fieldResult: any, uri, fieldResolve = null, funcFinished = null, headers: any = null) {
    // TODO !!!!!!!!!!!!!!!!!! TEMPORARY WORK AROUND FOR SG AS WE ARE GETTING 401 FROM GET REGISTER !!!!!!!!!!!!!!!!!!!!!!
    if (uri === 'register') {
      if (!headers)
        headers = {};
      if (APP_CONFIG.region.defaultCountryFrom === 'SG')
        headers = { ...headers, 'IsoCode': 'US' };
    }

    const funcPromise = () => {
      return this.getFields({ uri: uri}, false, headers);
    };
    return this.getFieldsPromise(fieldResult, funcPromise, fieldResolve, funcFinished);
  }

  async getFieldsPromise(fieldResult: any, funcPromise, fieldResolve = null, funcFinished = null) {
    fieldResult['initialized'] = true;
    fieldResult['func'] = funcPromise;
    if (!fieldResult.promise)
      fieldResult.promise = new Promise((resolve) => {
        fieldResult.resolve = resolve;  
        fieldResolve = fieldResult.resolve;
      });
    else if (!fieldResolve)
      fieldResolve = fieldResult.resolve;

    try {
      await funcPromise().then((fields) => { 
        if (fieldResult['func'] !== funcPromise)
          return;

        if (fields) {
          fieldResult['promise'] = null;
          fieldResult['resolve'] = null;
          fieldResult['fields'] = fields;
          if (funcFinished)
            funcFinished();
          fieldResolve();
        }
        else {
          window.setTimeout(() => {
            this.getFieldsPromise(fieldResult, funcPromise, fieldResolve, funcFinished);
          }, 10000);
        }
      });
    }
    catch (err) {
      if (fieldResult['func'] !== funcPromise)
        return;

      LoggerProvider.exception(this, err, LoggerEvent.ProviderException, 'Failed to get fields promise');
      if (err.status !== 401 && err.status !== 403) // these are already handled and shouldn't cause a reload
        window.setTimeout(() => {
          this.getFieldsPromise(fieldResult, funcPromise, fieldResolve, funcFinished);
        }, 10000);      
    }
    return fieldResult.promise;
  }

  initializeFields(force: boolean = false, loadedCallback = null, customerFieldsCallback = null) {
    if (this.initialized && !force)
      return null;
    
    this.getFieldsPromise(this.fieldsDynamicRegistration, async() => {      
      const resources = await this.resourceProvider.getResources();
      if (!this.resourceProvider.useResourcesEndpoint)
        resources.NewCustomerRequiredFields = await this.httpProvider.get(`${APP_CONFIG.API_URL}api/Customer/NewCustomerRequiredFields`);
      const res = resources.NewCustomerRequiredFields;

        UtilsProvider.updatePostalCodeRegex(res);
        if (loadedCallback)
          loadedCallback();
        if (customerFieldsCallback)
          customerFieldsCallback(res);
        return res;
    });
    if (APP_CONFIG.region.oldRemittanceLogin && force && (this.fieldsRegistration.func || this.fieldsRegistration.promise)) {
      return new Promise(async(resolve) => {
        await this.initializeFieldsAppType(this.fieldsRegistration);
        await this.fieldsRegistration.promise;
        this.initialized = true;
        if (loadedCallback)
          loadedCallback();
        resolve(true);
      });
    }
    
    this.initialized = true;
    return null;
  }  

  async initializeFieldsAppType(fields) {
    const deviceInfo = await this.deviceProvider.getDeviceInfo();
    const params = { ...deviceInfo };

    await this.httpProvider.initPromise;

    let headers: any = {
       'X-Forwarded-For': APP_CONFIG.region.regionalIp, // transmit the corresponding IP for the region, so the region-lock won't complain
    };
    // TODO !!!!!!!!!!!!!!!!!! TEMPORARY WORK AROUND FOR SG AS WE ARE GETTING 401 FROM GET REGISTER !!!!!!!!!!!!!!!!!!!!!!
    if (APP_CONFIG.region.defaultCountryFrom === 'SG')
      headers = { ...headers, 'IsoCode': 'US' };

    await this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}authorization/session`, params, (response: IApiAuthResponseEntity) => {
       let registerHeaders: any = {
         'Authorization': `Bearer ${response.authToken.jwtToken}`
       };
       this.getFieldsPromiseUrl(fields, 'register', fields.resolve, null, registerHeaders);
       return response;
    }, headers);  
  }

  async ensureInitializedField(fields, force: boolean = false, loadedCallback = null, customerFieldsCallback = null) {
    await FormControlProvider.readyPromise;
    const promise = this.initializeFields(force, loadedCallback, customerFieldsCallback);
    if (promise)
      return promise
    else if (fields && fields.promise)
      return fields.promise;
    return null;
  }

  async getFieldsDynamicRegistration(force: boolean = false): Promise<any> {
    await this.ensureInitializedField(this.fieldsDynamicRegistration, force);
    if (this.fieldsDynamicRegistration['fields'])
      return this.fieldsDynamicRegistration['fields'].Value;
    return null;
  }

  async getFieldsRegistration(force: boolean = false, checkIfChanged = false, responseCallback = null): Promise<IFormControl[]> {
    // special case when the JWT token after login doesn't have the IP iso code and hence the register call will fail
    // TODO: improve API
    if (!this.fieldsRegistration.func && !this.fieldsRegistration.promise) {
      // TODO !!!!!!!!!!!!!!!!!! TEMPORARY WORK AROUND FOR SG AS WE ARE GETTING 401 FROM GET REGISTER !!!!!!!!!!!!!!!!!!!!!!
      if (!HttpProvider.hasAuthIpIsoCode() || APP_CONFIG.region.defaultCountryFrom === 'SG') {
        LoggerProvider.info(this, 'Cannot use current session token to get registration fields - use an own session token for the call');
        if (this.fieldsRegistration.promise)
          this.initializeFieldsAppType(this.fieldsRegistration);
        else
          this.fieldsRegistration.promise = new Promise((resolve) => {
            this.fieldsRegistration.resolve = resolve;
            this.initializeFieldsAppType(this.fieldsRegistration);
          });      
      }
      else
        this.getFieldsPromiseUrl(this.fieldsRegistration, 'register');
      force = false;
    }
    if (checkIfChanged && this.fieldsRegistrationCountry !== APP_CONFIG.region.defaultCountryFrom) {
      LoggerProvider.info(this, 'Registration fields country has changed from ' + this.fieldsRegistrationCountry + ' to ' + APP_CONFIG.region.defaultCountryCodeFrom + ' => reload');
      force = true;
    }

    const country = APP_CONFIG.region.defaultCountryFrom;
    await this.ensureInitializedField(this.fieldsRegistration, force, () => {
      this.fieldsRegistrationCountry = country;
    } , responseCallback);
    return this.fieldsRegistration['fields'];
  }

  initializeCustomerSettingsFields(forceReload = false) {
    if (this.initializedCustomerSettings && !forceReload)
      return;
    
    // protection in the case lite users would accidentely land here
    if (this.httpProvider.getAuthIsLiteUser()) {
      return;
    }

    this.getFieldsPromiseUrl(this.fieldsCustomer, 'customer-profile');
    
    this.initializedCustomerSettings = true;
  }

  ensureCustomerInitializedField(fields, url) {
    if (fields && !fields.initialized)
      this.getFieldsPromiseUrl(fields, url);
    if (fields && fields.promise)
      return fields.promise;
    return null;
  }

  async getFieldsBank(): Promise<IFormControl[]> {
    await this.ensureCustomerInitializedField(this.fieldsCustomerBank, 'account/bank/create');
    return this.fieldsCustomerBank['fields'];
  }

  async getFieldsCard(): Promise<IFormControl[]> {
    await this.ensureCustomerInitializedField(this.fieldsCustomerCard, 'account/card/create');
    return this.fieldsCustomerCard['fields'];
  }

  async getFieldsIdCountry(): Promise<IFormControl[]> {
    await this.ensureCustomerInitializedField(this.fieldsCustomerIdCountry, 'identification/genericdetails');
    this.getFieldsIdType(APP_CONFIG.region.defaultCountryFrom);
    return this.fieldsCustomerIdCountry['fields'];
  }

  async getFieldsIdType(country): Promise<IFormControl[]> {
    if (country !== this.idTypeCountry) {
      this.idTypeCountry = country;
      await this.getFieldsPromiseUrl(this.fieldsCustomerIdType, 'identification/genericdetails/' + country);    
    }
    else if (this.fieldsCustomerIdType['promise'])
      await this.fieldsCustomerIdType['promise'];
    return this.fieldsCustomerIdType['fields'];
  }

  ensureCustomerSettingsInitializedField(fields) {
    this.initializeCustomerSettingsFields();
    if (fields && fields.promise)
      return fields.promise;
    return null;
  }

  async getFieldsCustomer(): Promise<IFormControl[]> {
    await this.ensureCustomerSettingsInitializedField(this.fieldsCustomer);
    return this.fieldsCustomer['fields'];
  }  

  async getPhoneNumberCountries() {
    const fields = await this.getFieldsCustomer();
    if (fields) {
      const field = fields.find((field) => (field.id === 'phoneNumberCountryCode'));
      if (field)
        return field.options;
    }
    return null;
  }

  async getPhoneNumberCountriesRegistration(filterTwilioEnabled = false) {
    let fields = await this.getFieldsRegistration();
    if (fields) {
      fields = JSON.parse(JSON.stringify(fields));
      const field = fields.find((field) => (field.id === 'phoneNumberCountryCode'));
      if (field){

        if (field.options.length > 0){
          if(!field.options[0].text.startsWith('(')){
            for (let i = 0; i < field.options.length; i++) {
              let item = field.options[i];
              item['text'] = item['text'].replace(item['selectedText'], '('+ item['selectedText']+')');
            }
          }
          if(filterTwilioEnabled === true && (<any>field.options[0]).twilioEnabled !== undefined){
            field.options = field.options.filter((country:any) => country.twilioEnabled === true ||  country.category === Region.SN) 
          }
        }
      }
      return field.options;
    }
    return null;
  }

  async getRecipientNameRegexes(countryTo?: string): Promise<{ regexes: { [fieldId: string]: string }, messages: { [fieldId: string]: string } }> {
    let localFields: any;

    if (countryTo) {
      // Country-specific: always fetch fresh using a local object to avoid race conditions
      localFields = {};
      const uri = `recipient/fields?countryTo=${countryTo}`;

      // Wrap in a timeout to prevent hanging forever if getFieldsPromise never resolves
      // (getFieldsPromise retries indefinitely on null/error responses without resolving)
      const fetchPromise = this.getFieldsPromiseUrl(localFields, uri);
      const timeoutPromise = new Promise<void>((_, reject) =>
        window.setTimeout(() => reject(new Error('getRecipientNameRegexes timed out')), 30000)
      );

      try {
        await Promise.race([fetchPromise, timeoutPromise]);
      } catch (err) {
        LoggerProvider.info(this, `getRecipientNameRegexes failed or timed out for countryTo=${countryTo}: ${err.message || err}`);
        return null;
      }
    } else {
      // Default (no country): use cached this.fieldsRecipient — fetches only once
      await this.ensureCustomerInitializedField(this.fieldsRecipient, 'recipient/fields');
      localFields = this.fieldsRecipient;
    }

    const fields: IFormControl[] = localFields['fields'];
    if (!fields) return null;

    const nameFieldIds = ['firstName', 'middleName', 'lastName1', 'lastName2'];
    const regexes: { [fieldId: string]: string } = {};
    const messages: { [fieldId: string]: string } = {};

    for (const fieldId of nameFieldIds) {
      const field = fields.find(f => f.id === fieldId);
      if (!field || !field.validators) {
        LoggerProvider.info(this, `No field or validators found for ${fieldId}`);
        continue;
      }

      const regexValidators = field.validators.filter(v => v.type === FormValidatorType.Regex);
      const regexValidator = regexValidators.length > 0 ? regexValidators[regexValidators.length - 1] : null;
      if (regexValidator && regexValidator.regex && regexValidator.regex.source) {
        regexes[fieldId] = regexValidator.regex.source;
        if (regexValidator.message) {
          messages[fieldId] = regexValidator.message;
        }
      } else {
        LoggerProvider.info(this, `No valid regex source for ${fieldId}, falling back to default`);
      }
    }

    return Object.keys(regexes).length > 0 ? { regexes, messages } : null;
  }

  async getRegistrationNameRegexes(): Promise<{ regexes: { [fieldId: string]: string }, messages: { [fieldId: string]: string } }> {
    try {
      const fields = await this.getFieldsRegistration();
      if (!fields) return null;

      // /register returns numeric field IDs (8601/17901/8701). Map them back to the
      // FormGroup control names used by registration-step2 so callers can index by control name.
      const beIdToControlName: { [beId: string]: string } = {
        [RegistrationFormDynamicField.FIRST_NAME]: 'firstName',
        [RegistrationFormDynamicField.MIDDLE_NAME]: 'middleName',
        [RegistrationFormDynamicField.LAST_NAME]: 'lastName',
      };
      const regexes: { [fieldId: string]: string } = {};
      const messages: { [fieldId: string]: string } = {};

      for (const beId of Object.keys(beIdToControlName)) {
        const controlName = beIdToControlName[beId];
        const field = fields.find(f => f.id === beId);
        if (!field || !field.validators) continue;

        const regexValidators = field.validators.filter(v => v.type === FormValidatorType.Regex);
        const regexValidator = regexValidators.length > 0 ? regexValidators[regexValidators.length - 1] : null;
        if (regexValidator && regexValidator.regex && regexValidator.regex.source) {
          regexes[controlName] = regexValidator.regex.source;
          if (regexValidator.message) {
            messages[controlName] = regexValidator.message;
          }
        }
      }

      return Object.keys(regexes).length > 0 ? { regexes, messages } : null;
    } catch (err) {
      LoggerProvider.info(this, `getRegistrationNameRegexes failed: ${err.message || err}`);
      return null;
    }
  }

  async getTopCountryListWithCountryCode() {
    const fields = await this.getFieldsRegistration();
    if (fields) {
      const countryWithPhone = fields.find((field) => (field.id === 'phoneNumberCountryCode')).options;
      const topCorridors = fields.find((field) => field.id === 'topCorridors').options;
      if (countryWithPhone && topCorridors){
        for (let i = 0; i < topCorridors.length; i++) {
          (<any>topCorridors[i]).isFromMobileTopup = true;
          countryWithPhone.find((ctyList: any) => {
            if(topCorridors[i].value === ctyList.category){
              topCorridors[i] = ctyList
              return true;
            };
          })
        }
        return topCorridors;
      }
    }
    return null;
  }
}



// WEBPACK FOOTER //
// ./src/providers/formbuilder/formcontrol.ts